import { BreadcrumbItem, Breadcrumbs } from '@heroui/react';
import { DB_TYPE } from '@quesmed/types';
import { useTheme } from 'next-themes';
import { useEffect, useRef } from 'react';
import { FaBarsStaggered } from 'react-icons/fa6';
import { FiDatabase, FiServer, FiSidebar } from 'react-icons/fi';
import { IoMdMoon, IoMdSunny } from 'react-icons/io';
import { matchPath, useLocation } from 'react-router-dom';
import { EEnvironment, ETheme, ROUTES } from '@/config';
import { cn } from '@/core/ui';
import { useAuthState, useGlobal } from '@/store';
import { ClientStorage } from '@/utils';
import { type ISelection, Select, Switch } from '../ui';

const whiteListedRoutes = [
  ROUTES.CONTENT.EDIT.QUESTION,
  ROUTES.OSCE.STATS,
  ROUTES.OSCE.SESSIONS,
  ROUTES.OSCE.SESSION_DETAIL,
  ROUTES.OSCE.STATIONS,
  ROUTES.OSCE.CALCULATOR,
  ROUTES.TUTOR.STATS,
  ROUTES.TUTOR.SESSIONS,
  ROUTES.TUTOR.SESSION_DETAIL,
  ROUTES.STUDY_GROUP.GROUPS,
  ROUTES.STUDY_GROUP.GROUP_DETAIL,
  ROUTES.STUDY_GROUP.SETTINGS,
  ROUTES.SSO.DASHBOARD,
  ROUTES.SSO.DETAIL,
  ROUTES.UNIVERSITY_ACCOUNTS.LIST,
  ROUTES.UNIVERSITY_ACCOUNTS.DETAIL,
  ROUTES.SETTINGS.PROMPTS,
  ROUTES.CONTENT.EDIT.CHAPTER,
  ROUTES.CONTENT.EDIT.STATION,
  ROUTES.CONTENT.REVIEW.COMMENTS,
  ROUTES.CONTENT.REVIEW.QUESTIONS,
  ROUTES.CONTENT.REVIEW.WEEKLY_SEARCH,
  ROUTES.CONTENT.REVIEW.WEEKLY_SQL_VIEW,
  ROUTES.CONTENT.REVIEW.QUERIES,
  ROUTES.AI.PROMPT_LAB,
  ROUTES.AI.HIGH_YIELD,
  ROUTES.CONTENT.JOB.LIST,
  ROUTES.CONTENT.JOB.DETAIL,
  ROUTES.CONTENT.JOB.CREATE,
  ROUTES.CONTENT.JOB.EXAMPLE,
  ROUTES.CONTENT.BLOG.MAIN,
  ROUTES.USER.INFO,
  ROUTES.USER.SUBS,
];

const dbHiddenRoutes = [ROUTES.MAIN, ROUTES.CONTENT.JOB.DETAIL];

export const Header = ({ breadCrumbs, className }: { breadCrumbs: string[]; className?: string }) => {
  const { setTheme, resolvedTheme } = useTheme();
  const dbType = useGlobal.use.dbType();
  const setDbType = useGlobal.use.setDbType();
  const setSideBar = useGlobal.use.setSideBar();
  const toggleSidebarCollapsed = useGlobal.use.toggleSidebarCollapsed();
  const environment = useGlobal.use.environment();
  const setEnvironment = useGlobal.use.setEnvironment();
  const { isSalesRole } = useAuthState();
  const location = useLocation();
  const dbSelectRef = useRef<HTMLSelectElement>(null);

  // add default value for ques-db if not exist
  useEffect(() => {
    const sessionDb = ClientStorage.session.getItem<DB_TYPE>('ques-dB');
    if (!sessionDb) {
      ClientStorage.session.setItem('ques-dB', dbType);
    } else {
      setDbType(sessionDb);
    }
  }, []);

  // Load environment from localStorage on component mount
  useEffect(() => {
    const savedEnv = ClientStorage.local.getItem<EEnvironment>('ques-envMode');
    if (savedEnv) {
      setEnvironment(savedEnv);
    } else {
      // Default to production if not set
      ClientStorage.local.setItem('ques-envMode', EEnvironment.PRODUCTION);
    }
  }, []);

  // Reset DB selection and show error on route change
  useEffect(() => {
    const isWhiteListedRoute = whiteListedRoutes.some((route) => matchPath(route, location.pathname));
    if (location.pathname !== ROUTES.MAIN && !isWhiteListedRoute) {
      setDbType(undefined as never);
      setTimeout(() => {
        dbSelectRef.current?.focus();
      }, 100);
    } else {
      const sessionDb = ClientStorage.session.getItem<DB_TYPE>('ques-dB');
      setDbType(sessionDb ? sessionDb : (dbType ?? DB_TYPE.FINALS));
    }
  }, [location.pathname]);

  const handleThemeChange = (isSelected: boolean) => {
    setTheme(() => (isSelected ? ETheme.DARK : ETheme.LIGHT));
  };

  // const handleThemeChange = (keys: Selection) => {
  //   const val = keys ? Array.from(keys).join('') : keys;
  //   setTheme(() => val);
  // };

  const handleDbChange = (keys: ISelection) => {
    const val = keys ? Array.from(keys).join('') : keys;
    const newDbType = val ? Number(val) : DB_TYPE.FINALS;
    setDbType(newDbType);
  };

  const handleEnvChange = (keys: ISelection) => {
    const val = keys ? Array.from(keys).join('') : keys;
    const newEnv = (val as EEnvironment) || EEnvironment.PRODUCTION;
    setEnvironment(newEnv);
    ClientStorage.local.setItem('ques-envMode', newEnv);
    // Reload the page to apply the new environment
    window.location.reload();
  };

  const isDbHiddenRoute = dbHiddenRoutes.some((route) => matchPath(route, location.pathname));

  return (
    <div
      className={cn(
        'z-5 bg-background px-6 w-full shadow-md',
        'flex flex-row items-center shrink-0 justify-between',
        className,
      )}
    >
      <div className='flex flex-row items-center'>
        <FaBarsStaggered onClick={() => setSideBar(true)} className='w-6 h-6 lg:hidden mr-5' />

        <FiSidebar
          onClick={toggleSidebarCollapsed}
          className='w-6 h-6 hidden lg:block mr-5 cursor-pointer hover:opacity-70 transition-opacity'
        />
        {!!breadCrumbs.length && (
          <Breadcrumbs>
            {breadCrumbs.map((b) => (
              <BreadcrumbItem className='capitalize' key={b}>
                {b}
              </BreadcrumbItem>
            ))}
          </Breadcrumbs>
        )}
      </div>
      <div className='flex flex-row space-x-4 items-center'>
        {/* <Dropdown placement='bottom-end'>
          <DropdownTrigger>
            <button type='button' className='p-2.5 rounded-md border border-divider'>
              {theme === ETheme.LIGHT ? (
                <IoMdSunny className={'w-5 h-5'} />
              ) : (
                <IoMdMoon className={'w-5 h-5'} />
              )}
            </button>
          </DropdownTrigger>
          <DropdownMenu
            selectionMode='single'
            selectedKeys={[theme || '']}
            onSelectionChange={handleThemeChange}
            aria-label='Static Actions'
          >
            <DropdownItem key={ETheme.LIGHT}>Light</DropdownItem>
            <DropdownItem key={ETheme.DARK}>Dark</DropdownItem>
            <DropdownItem key={ETheme.MODERN}>Modern</DropdownItem>
            <DropdownItem key={ETheme.SYSTEM}>System</DropdownItem>
          </DropdownMenu>
        </Dropdown> */}

        <Switch
          size='lg'
          isSelected={resolvedTheme === ETheme.DARK}
          onValueChange={handleThemeChange}
          thumbIcon={({ isSelected, className }: { isSelected: boolean; className: string }) =>
            isSelected ? <IoMdMoon className={className} /> : <IoMdSunny className={className} />
          }
        />

        {!isSalesRole && !isDbHiddenRoute && (
          <>
            <div className='relative'>
              <Select
                ref={dbSelectRef}
                aria-label='target db'
                className={cn('w-24 md:w-46')}
                startContent={<FiDatabase className='w-6 h-6 mr-1' />}
                onSelectionChange={handleDbChange}
                isInvalid={!dbType}
                selectedKeys={dbType ? [`${dbType}`] : []}
                // errorMessage={dbError}
                options={[
                  { key: DB_TYPE.FINALS, label: 'UNDERGRAD' },
                  { key: DB_TYPE.MRCP, label: 'MRCP' },
                  { key: DB_TYPE.UCAT, label: 'UCAT' },
                ]}
              />
              {!dbType && (
                <div className='absolute top-9 right-0 w-44'>
                  <span className='text-danger-500 text-xs'>Please select a database</span>
                </div>
              )}
            </div>
            <Select
              aria-label='environment'
              className='w-24 md:w-46'
              startContent={<FiServer className='w-6 h-6 mr-1' />}
              onSelectionChange={handleEnvChange}
              selectedKeys={[environment]}
              options={Object.entries(EEnvironment).map(([key, value]) => ({
                key: value,
                label: key,
              }))}
            />
          </>
        )}
      </div>
    </div>
  );
};
